import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneCall, ArrowUpRight } from 'lucide-react';
import * as jobsApi from '@/api/jobs.api';
import * as installationApi from '@/api/installation.api';
import * as employeesApi from '@/api/employees.api';
import type { JobWithRelations } from '@/api/jobs.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';

/**
 * S-04 · Los dos finales posibles de un CASO REMOTO (tanda EMT-CNV-2026-01):
 * se resolvió por teléfono y se cierra, o no se resolvió y escala a visita.
 */

/** Cierre del caso atendido a distancia, con su tipificación. */
export function CerrarCasoRemotoModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [resultado, setResultado] = useState('');
  const [nivel, setNivel] = useState('');
  const [causa, setCausa] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cfg = useQuery({
    queryKey: ['installation', 'crm'],
    queryFn: installationApi.getCrmView,
    enabled: Boolean(job),
  });
  const niveles = (cfg.data?.trabajos?.nivelesResolucion ?? []).filter((n) => n.activo);
  const causas = (cfg.data?.trabajos?.causasRaiz ?? []).filter((c) => c.activo);

  useEffect(() => {
    if (!job) return;
    setResultado('');
    // La mayoría de los casos remotos son N1 (se resolvió sin ir), pero no se
    // pre-elige: que el operador lo diga es el dato que hace útil el reporte.
    setNivel('');
    setCausa('');
    setError(null);
  }, [job]);

  const cerrar = useMutation({
    mutationFn: () =>
      jobsApi.cerrarCasoRemoto(job!.id, {
        resultado: resultado.trim(),
        nivelResolucion: nivel,
        causaRaiz: causa,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('Caso cerrado');
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo cerrar el caso'),
  });

  function submit() {
    if (!resultado.trim()) {
      setError('Escriba qué se hizo para resolver el caso.');
      return;
    }
    if (!nivel || !causa) {
      setError('Indique cómo se resolvió y por qué falló.');
      return;
    }
    setError(null);
    cerrar.mutate();
  }

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="Cerrar el caso"
      description="Se resolvió sin ir al sitio. Queda registrado como garantía atendida a distancia."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button data-guia="remoto.confirmar" onClick={submit} loading={cerrar.isPending}>
            <PhoneCall className="mr-1 h-4 w-4" />
            Cerrar el caso
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Textarea
          data-guia="remoto.que-se-hizo"
          label="¿Qué se hizo?"
          placeholder="Se guio al cliente por teléfono para reconectar el artículo al wifi"
          rows={3}
          value={resultado}
          onChange={(e) => setResultado(e.target.value)}
        />
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            ¿Cómo se resolvió?
          </span>
          <select
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            value={nivel}
            onChange={(e) => setNivel(e.target.value)}
          >
            <option value="">Elija una opción…</option>
            {niveles.map((n) => (
              <option key={n.id} value={n.codigo}>
                {n.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            ¿Por qué falló?
          </span>
          <select
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            value={causa}
            onChange={(e) => setCausa(e.target.value)}
          >
            <option value="">Elija una opción…</option>
            {causas.map((c) => (
              <option key={c.id} value={c.nombre}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

/** Escalamiento del caso a visita de campo: mismo trabajo, mismo historial. */
export function EscalarCasoModal({
  job,
  onClose,
}: {
  job: JobWithRelations | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [employeeId, setEmployeeId] = useState('');
  const [cuando, setCuando] = useState('');
  const [error, setError] = useState<string | null>(null);

  const tecnicos = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesApi.listEmployees(),
    enabled: Boolean(job),
  });

  useEffect(() => {
    if (!job) return;
    setEmployeeId('');
    setCuando('');
    setError(null);
  }, [job]);

  const escalar = useMutation({
    mutationFn: () =>
      jobsApi.escalarCasoRemoto(job!.id, {
        employeeId: employeeId || null,
        // El input datetime-local entrega hora LOCAL sin zona; se convierte a
        // instante acá, que es donde sabemos en qué zona está el operador.
        scheduledAt: cuando ? new Date(cuando).toISOString() : null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['jobs'] });
      toast.success('El caso pasó a visita de campo');
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'No se pudo escalar'),
  });

  return (
    <Modal
      open={Boolean(job)}
      onClose={onClose}
      title="Mandar un técnico"
      description="El caso pasa a ser una visita. Se conserva todo lo que ya se registró."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            data-guia="escalar.confirmar"
            onClick={() => escalar.mutate()}
            loading={escalar.isPending}
          >
            <ArrowUpRight className="mr-1 h-4 w-4" />
            Mandar técnico
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Técnico
          </span>
          <select
            className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
          >
            <option value="">Sin asignar por ahora</option>
            {(tecnicos.data?.items ?? [])
              .filter((t) => t.activo)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.nombre}
                </option>
              ))}
          </select>
        </label>
        <Input
          label="Fecha y hora de la visita"
          type="datetime-local"
          value={cuando}
          onChange={(e) => setCuando(e.target.value)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </div>
    </Modal>
  );
}
